package com.vamvisions.main.components

import androidx.compose.runtime.Composable
import com.vamvisions.main.navigation.ExternalLink
import com.vamvisions.main.portfolio.presentation.common.SkillUi
import com.vamvisions.main.theme.AppStyleSheet
import com.vamvisions.main.theme.Tokens
import com.vamvisions.main.theme.cssVar
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement
import kotlin.math.max

/**
 * The skills as wrapping pills, links that open in a new tab. Each description is a tooltip shown on hover
 * and keyboard focus, and is also the link's accessible description. [ownerId] must be unique on the
 * page, because the same skill appears under several owners and each tooltip needs its own id.
 */
@Composable
fun SkillList(ownerId: String, skills: List<SkillUi>) {
    if (skills.isEmpty()) return
    Ul(attrs = { classes(AppStyleSheet.skillList) }) {
        skills.forEach { skill ->
            val tooltipId = "skill-$ownerId-${skill.slug}"
            Li(attrs = { classes(AppStyleSheet.skillItem) }) {
                ExternalLink(
                    href = skill.url,
                    attrs = {
                        classes(AppStyleSheet.skillLink)
                        attr("aria-describedby", tooltipId)
                        onMouseEnter { placeTooltip(it.target.unsafeCast<HTMLElement>()) }
                        onFocus { placeTooltip(it.target.unsafeCast<HTMLElement>()) }
                    },
                ) {
                    Text(skill.name)
                }
                Span(attrs = {
                    id(tooltipId)
                    classes(AppStyleSheet.skillTooltip)
                    attr("role", "tooltip")
                }) {
                    Text(skill.description)
                }
            }
        }
    }
}

/**
 * Places [link]'s tooltip just below its pill, over any rows beneath, and centres it under the pill, kept inside the
 * list, so it sits left, centre or right like the pill. Called as the tooltip is shown, because only then can its
 * width be measured.
 */
private fun placeTooltip(link: HTMLElement) {
    val tooltip = link.nextElementSibling?.unsafeCast<HTMLElement>() ?: return
    val list = link.closest("ul") ?: return
    val listBox = list.getBoundingClientRect()
    val linkBox = link.getBoundingClientRect()
    val width = tooltip.getBoundingClientRect().width
    val centre = linkBox.left + linkBox.width / 2 - listBox.left
    val left = (centre - width / 2).coerceIn(0.0, max(0.0, listBox.width - width))
    tooltip.style.left = "${left}px"
    tooltip.style.top = "calc(${linkBox.bottom - listBox.top}px + ${cssVar(Tokens.SPACE_1)})"
}
